import apiClient from '@api-client';
import { Schemas } from '@api-client/generated/types';
import { useQuery } from '@tanstack/react-query';

type Membership = Schemas.AdminMembershipRecordDto;
type AdminRole = NonNullable<Schemas.User['adminRole']>;

const CSM_ROLES: readonly AdminRole[] = [
  'admin',
  'accountant',
  'kyc',
  'support',
];

const PER_PAGE = 100;
// Safety backstop so a runaway roster can't spin forever. CSM memberships are
// inherently bounded (internal staff × the clients they serve); if a role ever
// exceeds this we log rather than silently truncate.
const MAX_PAGES_PER_ROLE = 50;

/**
 * The membership read contract has no "any admin role" filter and returns every
 * companies_users row (client users included) when unfiltered. To get the
 * CSM-only cross-company set we union the four role-filtered queries — each
 * returns only that role's memberships, and a user has exactly one role, so
 * there are no duplicates. Every page of each is fetched so the set is complete
 * (never just the first page).
 */
const fetchAllForRole = async (role: AdminRole): Promise<Membership[]> => {
  const rows: Membership[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await apiClient.AdminCompanyController_listMemberships({
      parameter: { role, page, perPage: PER_PAGE },
    });
    rows.push(...response.records);
    totalPages = response.metadata.totalPages;
    page += 1;
  } while (page <= totalPages && page <= MAX_PAGES_PER_ROLE);

  if (totalPages > MAX_PAGES_PER_ROLE) {
    console.warn(
      `useAccessMap: ${role} memberships exceed ${MAX_PAGES_PER_ROLE} pages; results truncated.`
    );
  }

  return rows;
};

export const ACCESS_MAP_QUERY_KEY = ['accessMap', 'memberships'];

/**
 * All CSM company memberships across every client, fetched once and aggregated
 * client-side into the by-employee / by-company cuts of the Access Map.
 */
export const useAccessMap = () => {
  const query = useQuery<Membership[]>({
    queryKey: ACCESS_MAP_QUERY_KEY,
    // Up to 4 roles × many pages per load — keep it fresh for a minute so
    // remounts and window refocus don't refire the whole fan-out.
    staleTime: 60_000,
    queryFn: async () => {
      const perRole = await Promise.all(CSM_ROLES.map(fetchAllForRole));
      return perRole.flat();
    },
  });

  return {
    memberships: query.data ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
};
